import React, { useState, ReactNode } from "react";
import { AuthStateProps, AcquireTokenFunction } from "@konecorp/ui-library";

import Context, {
  BreadcrumbsType,
  BusinessType,
  ContextState,
  ErrorMessage,
  QualificationsType,
  TrainingTypes,
  UserPreferenceType,
} from "./index";
import { TrainingHeadersType } from "../components/TrainingsHeaderView";
import i18n from "../i18n";

type ReactProps = {
  children: ReactNode;
};

export default ({ children }: ReactProps): JSX.Element => {
  const authData = {
    isAuthenticated: false,
  } as AuthStateProps;

  const [authState, updateAuthState] = useState<AuthStateProps>(authData);

  const [isLoading, updateIsLoading] = useState<boolean>(false);
  const [errorMessage, updateErrorMessage] = useState<ErrorMessage | null>(
    null,
  );
  const [loadingStack, setLoadingStack] = React.useState<string[]>([]);
  const legacyLoadingTokensRef = React.useRef<string[]>([]);

  const beginLoading = React.useCallback((token?: string) => {
    const resolvedToken = token || `loading-${Date.now()}-${Math.random()}`;
    setLoadingStack((prev) => [...prev, resolvedToken]);
    return resolvedToken;
  }, []);

  /**
   * Ends a loading operation and removes it from the loading stack.
   * 
   * @param token - Optional token identifying a specific loading operation to remove.
   *                If provided, removes the last occurrence of this token from the stack.
   *                If not provided, removes the most recent loading operation (LIFO).
   * 
   * @remarks
   * - If the stack is empty, no operation is performed.
   * - If a token is provided but not found in the stack, no operation is performed.
   * - This is a memoized callback with no external dependencies.
   */
  const endLoading = React.useCallback((token?: string) => {
    setLoadingStack((prev) => {
      if (prev.length === 0) return prev;
      if (token) {
        const removeIndex = prev.lastIndexOf(token);
        if (removeIndex < 0) return prev;
        return [...prev.slice(0, removeIndex), ...prev.slice(removeIndex + 1)];
      }
      return prev.slice(0, -1);
    });
  }, []);

  const setLoading = React.useCallback(
    (status: boolean) => {
      if (status) {
        const legacyToken = `legacy-loading-${Date.now()}-${Math.random()}`;
        legacyLoadingTokensRef.current.push(legacyToken);
        beginLoading(legacyToken);
        return;
      }

      const tokenToClear = legacyLoadingTokensRef.current.pop();
      if (tokenToClear) {
        endLoading(tokenToClear);
        return;
      }

      endLoading();
    },
    [beginLoading, endLoading],
  );

  const withLoading = React.useCallback(
    async <T,>(callback: () => Promise<T>, token?: string): Promise<T> => {
      const loadingToken = beginLoading(token);
      try {
        return await callback();
      } finally {
        endLoading(loadingToken);
      }
    },
    [beginLoading, endLoading],
  );

  const loading = loadingStack.length > 0;

  const [acquireToken, updateAcquireToken] = useState<
    AcquireTokenFunction | undefined
  >();

  const [countryList, setCountryList] = useState<string[]>([]);
  const [country, setCountry] = React.useState<string[]>([]);
  const [trainingHeaders, setTrainingHeaders] = useState<TrainingHeadersType[]>(
    [],
  );
  const [frontline, setFrontline] = React.useState<string[]>([]); // Default value TO BE CHANGED
  const [frontlineId, setFrontlineId] = React.useState<string>(""); // Default value TO BE CHANGED
  const [regionId, setRegionId] = React.useState<string>(""); // Default value TO BE CHANGED
  const [supplierType, setSupplierType] = React.useState<string>(""); // Default value TO BE CHANGED
  const [formattedCountryCodes, setFormattedCountryCodes] = React.useState<
    string[]
  >([]); // Default value TO BE CHANGED
  const [blockReason, setBlockReason] = React.useState<string>(""); // Default value TO BE CHANGED
  const [bulkUploadStatus, setBulkUploadStatus] =
    React.useState<boolean>(false); // Default value TO BE CHANGED
  const [alertState, setAlertState] = React.useState<
    "success" | "error" | "info" | "warning" | ""
  >(""); // Default value TO BE CHANGED
  const [alertMessage, setAlertMessage] = React.useState<string>(""); // Default value TO BE CHANGED
  const [userFirstName, setUserFirstName] = React.useState<string>(""); // Default value TO BE CHANGED
  const [userLastName, setUserLastName] = React.useState<string>(""); // Default value TO BE CHANGED
  const [userPreference, setUserPreference] =
    React.useState<UserPreferenceType>({
      // Seed from the persisted i18n language (localStorage) so a page refresh keeps the user's
      // chosen language instead of resetting to en-US. Settings/admin pages still override it
      // from the backend preference when they load.
      languagePreference: i18n.language || "en-US",
    });

  const [qualificationsList, setQualificationsList] = React.useState<
    QualificationsType[]
  >([]); // Default value TO BE CHANGED
  const [passportTypeId, setPassportTypeId] = React.useState<number>(0); // Default value TO BE CHANGED
  const [qualificationTypeId, setQualificationTypeId] =
    React.useState<number>(0); // Default value TO BE CHANGED
  const [trainingTypes, setTrainingTypes] = React.useState<TrainingTypes>(
    {} as TrainingTypes,
  ); // Default value TO BE CHANGED
  const [bulkUploadMode, setBulkUploadMode] = React.useState<
    "upload" | "download"
  >("upload"); // Default value TO BE CHANGED
  const [breadcrumbs, setBreadcrumbs] = React.useState<BreadcrumbsType[]>([]);
  const [isBreadcrumbClicked, setIsBreadcrumbClicked] =
    React.useState<boolean>(false);
  // Business Type state - default to empty (user must select)
  const [selectedBusinessType, setSelectedBusinessType] =
    React.useState<string>("");
  const [selectedBusinessTypeId, setSelectedBusinessTypeId] =
    React.useState<number>(0);
  const [businessTypesList, setBusinessTypesList] = React.useState<
    BusinessType[]
  >([]);
  const [isReview, setIsReview] = React.useState<boolean>(false); // State to determine if user is in review mode
  const [isAgreed, setIsAgreed] = React.useState<boolean>(false); // State to track if user has agreed to terms
  const [statusEndDate, setStatusEndDate] = React.useState<string>(""); // Default value TO BE CHANGED
  const [adminRole, setAdminRole] = React.useState<string | null>(null);
  const [permissions, setPermissions] = React.useState<string[]>([]);

  const state: ContextState = {
    authState,
    isLoading,
    acquireToken,
    errorMessage,
    countryList,
    country,
    trainingHeaders,
    loading,
    setLoading,
    loadingStack,
    beginLoading,
    endLoading,
    withLoading,
    setTrainingHeaders,
    setCountry,
    setCountryList,
    updateAuthState,
    updateIsLoading,
    updateAcquireToken,
    updateErrorMessage,
    frontline,
    setFrontline,
    frontlineId,
    regionId,
    setRegionId,
    setFrontlineId,
    supplierType,
    setSupplierType,
    formattedCountryCodes,
    setFormattedCountryCodes,
    blockReason,
    setBlockReason,
    bulkUploadStatus,
    setBulkUploadStatus,
    alertState,
    setAlertState,
    alertMessage,
    setAlertMessage,
    userFirstName,
    setUserFirstName,
    userLastName,
    setUserLastName,
    userPreference,
    setUserPreference,
    qualificationsList,
    setQualificationsList,
    passportTypeId,
    setPassportTypeId,
    qualificationTypeId,
    setQualificationTypeId,
    trainingTypes,
    setTrainingTypes,
    bulkUploadMode,
    setBulkUploadMode,
    breadcrumbs,
    setBreadcrumbs,
    isBreadcrumbClicked,
    setIsBreadcrumbClicked,
    selectedBusinessType,
    setSelectedBusinessType,
    selectedBusinessTypeId,
    setSelectedBusinessTypeId,
    businessTypesList,
    setBusinessTypesList,
    isReview,
    setIsReview,
    isAgreed,
    setIsAgreed,
    statusEndDate,
    setStatusEndDate,
    adminRole,
    permissions,
    setPermissions,
    setAdminRole,
  };

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useGlobalStateContext = () => {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error("useGlobalState must be used within a GlobalStateProvider");
  }
  return context;
};
